import React, { useState, useRef, useEffect } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import classes from "./Map.module.css";

const fetchGeoJson = async (fileName: string) => {
	const response = await fetch(`${process.env.PUBLIC_URL}/data/${fileName}`);
	if (!response.ok) {
		throw new Error("Network response was not ok");
	}
	return await response.json();
};

interface OrderCountByCity {
	city: string;
	orderCount: number;
}

interface MapProps {
	ordersCountByCity: OrderCountByCity[];
	countryId?: number | any;
	onCityHover?: (countryId: number, cityName: string) => void;
	hoveredCity?: string;
	selectedToggle?: "dorëzuar" | "kthyer"; //
}

const Map: React.FC<MapProps> = ({
	ordersCountByCity,
	countryId,
	onCityHover,
	hoveredCity,
	selectedToggle,
}) => {
	const [tooltipContent, setTooltipContent] = useState("");
	const [tooltipVisible, setTooltipVisible] = useState(false);
	const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
	const mapContainerRef = useRef<HTMLDivElement>(null);
	const [geoJson, setGeoJson] = useState<any>(null);

	useEffect(() => {
		const loadGeoJson = async () => {
			try {
				let fileName = "";
				switch (countryId) {
					case 1:
						fileName = "kosovo.json";
						break;
					case 2:
						fileName = "albania.json";
						break;
					case 3:
						fileName = "macedonia.json";
						break;
					default:
						break;
				}
				if (fileName) {
					const data = await fetchGeoJson(fileName);
					setGeoJson(data);
				}
			} catch (error) {
				console.error("Error fetching GeoJSON:", error);
			}
		};
		loadGeoJson();
	}, [countryId]);

	const calculatePercentage = (cityName: string) => {
		const cityData = ordersCountByCity.find(
			(city) => city.city === cityName
		);
		const totalOrders = ordersCountByCity.reduce(
			(sum, city) => sum + city.orderCount,
			0
		);
		return cityData
			? ((cityData.orderCount / totalOrders) * 100).toFixed(2)
			: "0";
	};

	useEffect(() => {
		if (hoveredCity) {
			const cityData = ordersCountByCity.find(
				(city) => city.city === hoveredCity
			);
			if (cityData) {
				const percentage = calculatePercentage(hoveredCity);
				setTooltipContent(
					`<div style="display: flex; flex-direction: column; gap: 0.5rem; padding: 1rem;">
            <p style="color: #828282;"><strong style="color: #828282;">${hoveredCity}</strong></p>
            <div style="display: flex; gap: 1rem; align-items: center;">
              <p>${cityData.orderCount}</p> 
              <p style="background: #F9EBEB; color: #36B37E; padding: 0.2rem">+${percentage}%</p>
            </div>
          </div>`
				);
			} else {
				setTooltipContent(`<div style="display: flex; flex-direction: column; gap: 0.5rem; padding: 1rem;">
          <p style="color: #828282;"><strong>${hoveredCity}</strong></p>
          <div style="display: flex; gap: 1rem; align-items: center;">
          <p>0</p> 
          </div>`);
			}
			setTooltipVisible(true);
		} else {
			setTooltipVisible(false);
		}
	}, [hoveredCity, ordersCountByCity]);

	const handleMouseEnter = (
		geo: any,
		event: React.MouseEvent<SVGPathElement, MouseEvent>
	) => {
		const cityData = ordersCountByCity.find(
			(city) => city.city === geo.properties.VARNAME_2
		);

		if (cityData) {
			const percentage = calculatePercentage(geo.properties.VARNAME_2);
			setTooltipContent(
				`<div style="display: flex; flex-direction: column; gap: 0.5rem; padding: 1rem;">
          <p style="color: #828282;"><strong>${geo.properties.VARNAME_2}</strong></p>
          <div style="display: flex; gap: 1rem; align-items: center;">
            <p>${cityData.orderCount}</p> 
            <p style="background: #F9EBEB; color: #36B37E; padding: 0.2rem">+${percentage}%</p>
          </div>
        </div>`
			);
		} else {
			setTooltipContent(`<div style="display: flex; flex-direction: column; gap: 0.5rem; padding: 1rem;">
        <p style="color: #828282;"><strong>${geo.properties.VARNAME_2}</strong></p>
        <div style="display: flex; gap: 1rem; align-items: center;">
        <p>0</p> 
        </div>`);
		}

		const mapContainerRect =
			mapContainerRef.current?.getBoundingClientRect();
		if (mapContainerRect) {
			setTooltipPosition({
				x: event.clientX - mapContainerRect.left,
				y: event.clientY - mapContainerRect.top,
			});
		}

		setTooltipVisible(true);
		if (onCityHover) {
			onCityHover(countryId, geo.properties.VARNAME_2);
		}
	};

	const handleMouseLeave = () => {
		setTooltipVisible(false);
		if (onCityHover) {
			onCityHover(countryId, "");
		}
	};

	const getMapConfig = (countryId: number) => {
		switch (countryId) {
			case 1:
				return {
					geography: geoJson,
					center: [21, 42.5],
					scale: 16500,
				};
			case 2:
				return {
					geography: geoJson,
					center: [20, 41],
					scale: 7700,
				};
			case 3:
				return {
					geography: geoJson,
					center: [21.7, 41.6],
					scale: 12000,
				};
			default:
				return {
					geography: null,
					center: [0, 0],
					scale: 1000,
				};
		}
	};

	const { geography, center, scale } = getMapConfig(countryId);

	if (!geoJson) return <div>Loading...</div>;

	return (
		<div
			className="map-container"
			style={{ position: "relative" }}
			ref={mapContainerRef}
		>
			<ComposableMap
				projection="geoMercator"
				className={classes.responsiveMap}
				projectionConfig={{ scale, center }}
			>
				<Geographies geography={geography}>
					{({ geographies }: any) =>
						geographies.map((geo: any) => (
							<Geography
								key={geo.rsmKey}
								geography={geo}
								onMouseEnter={(event: any) =>
									handleMouseEnter(geo, event)
								}
								onMouseLeave={handleMouseLeave}
								style={{
									default: {
										fill:
											geo.properties.VARNAME_2 ===
											hoveredCity
												? selectedToggle === "dorëzuar"
													? "#36B37E"
													: "#B33636"
												: selectedToggle === "dorëzuar"
												? "#EBF9F3"
												: "#F9EBEB",
										stroke:
											selectedToggle === "dorëzuar"
												? "#36B37E"
												: "#F7A9A9",
										strokeWidth: 2,
										strokeDasharray: "6,12",
									},
									hover: {
										fill:
											selectedToggle === "dorëzuar"
												? "#36B37E"
												: "#B33636",
										outline: "none",
									},
									pressed: {
										fill: "none",
										outline: "none",
										border: "none",
									},
								}}
							/>
						))
					}
				</Geographies>
			</ComposableMap>
			{tooltipVisible && (
				<div
					style={{
						position: "absolute",
						top: tooltipPosition.y + 10,
						left: tooltipPosition.x + 10,
						backgroundColor: "#FFFFFF",
						boxShadow: "0px 4px 20px rgba(83, 83, 83, 0.16)",
						borderRadius: "4px",
						color: "#000",
						padding: "5px 10px",
						pointerEvents: "none",
						whiteSpace: "nowrap",
						zIndex: 1000,
					}}
					dangerouslySetInnerHTML={{ __html: tooltipContent }}
				/>
			)}
		</div>
	);
};

export default Map;
